import FolderZipIcon from "@mui/icons-material/FolderZip";
import { Box, Container } from "@mui/material";
import axios from "axios";
import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "react-toastify";
import { useRecoilState } from "recoil";
import { Contract, ContractService } from "../../backend";
import { useUpcxChain } from "../../hook/useUpcxChain";
import { ShowProgress } from "../../state/ShowProgress";
import { ThemeLight } from "../../state/ThemeLight";

export const VerifyContract = () => {
  const navigate = useNavigate();
  const { contractName } = useParams();

  const { selectedShard } = useUpcxChain();

  const [, setShowProgress] = useRecoilState(ShowProgress);
  const [themeLight] = useRecoilState(ThemeLight);

  const [contract, setContract] = useState<Contract>();

  const [file, setFile] = useState<File | undefined>();

  useEffect(() => {
    if (!selectedShard) {
      return;
    }

    getContract();
  }, [contractName, selectedShard]);

  const getContract = () => {
    if (!contractName) return;

    ContractService.getContract(contractName)
      .then((res) => {
        if (!res) return;

        setContract(res);
      })
      .catch((err) => {
        console.log(err);
      });
  };

  const openFileDlg = () => {
    document.getElementById("source_file")?.click();
  };

  const handleVerify = () => {
    if (!file) {
      toast.warn("Please select the source code project file.", {
        theme: themeLight ? "light" : "dark",
      });
      return;
    }

    setShowProgress(true);
    let data = new FormData();
    data.append("file", file);

    axios
      .postForm(
        `${selectedShard?.apiUrl}/contract/verify/${contractName}`,
        data
      )
      .then((res) => {
        setShowProgress(false);
        if (res.data === true) {
          toast.success("Verification has been completed successfully.", {
            theme: themeLight ? "light" : "dark",
          });
          navigate(`/contract/${contractName}`);
        } else {
          toast.error("Verification has been failed.", {
            theme: themeLight ? "light" : "dark",
          });
        }
      })
      .catch((err) => {
        setShowProgress(false);
        console.log(err);
      });
  };

  return (
    <Container className="container-xxl pt-5 pb-12">
      <Box className="row justify-content-center">
        <Box className="col-lg-6 text-center">
          <h1 className="h4 mb-3">Verify &amp; Publish Contract Source Code</h1>
          <p>
            Source code verification provides transparency for users interacting
            with smart contracts. By uploading the source code, UPCX Block
            Explorer will match the compiled code with that on the blockchain. .
          </p>
          <p>
            This is an <b>experimental</b> source code verifier which supports
            verification of <b>Upcx Blockchain Smart Contract files</b>{" "}
            (imports).
          </p>
        </Box>
      </Box>
      <Box className="row justify-content-center">
        <Box className="col-lg-8 col-xl-7">
          <Box id="ContentPlaceHolder1_BoxVerifyContract">
            <span id="ContentPlaceHolder1_lblErrorStatus"></span>
            <Box id="ContentPlaceHolder1_maindisplay">
              <Box className="card mb-3">
                <Box className="card-header d-flex align-items-center justify-content-between position-relative border-0 gap-3">
                  <h2 className="card-header-title">
                    Upload Contract Source Code
                  </h2>
                </Box>
                <Box className="collapse show" id="collapseUploadSourceCode1">
                  <Box className="card-body border-top">
                    <ol className="list-xs-space ps-4 mb-4">
                      <li>
                        If it compiles correctly at local environment , it
                        should also compile correctly here.
                      </li>
                      <li>
                        As this is an beta release module, there is limited
                        support for external libraries.
                      </li>
                      <li>
                        There is a timeout of up to 45 seconds for each contract
                        compiled. If you need longer compilation times (up to 3
                        mins).
                      </li>
                    </ol>

                    <Box className="card bg-light shadow-none rounded p-3 mb-4">
                      <Box className="row mb-2">
                        <Box className="col-xl-3 text-muted">
                          Contract Address:
                        </Box>
                        <Box className="col-xl-8 fw-medium">
                          <a
                            target="_blank"
                            href="/address/0x059D89E8E3dBE0009f4a9CFA0A8265579CcCF669"
                          >
                            {contractName}
                          </a>
                        </Box>
                      </Box>
                    </Box>
                    <h4 className="h6 fw-normal mb-2">
                      <b>
                        Please select the Smart Contract project zipped (*.zip)
                        file for upload
                      </b>{" "}
                      <span className="text-danger">*</span>
                    </h4>
                    <Box className="border border-dashed border-2 text-center rounded p-10">
                      <label className="form-label mb-2">
                        <FolderZipIcon className="fal fa-file-code fs-3" />
                        <Box className="mt-3">
                          Click here to select project file
                        </Box>
                        <Box className="small text-muted">
                          Supported file types: .zip
                        </Box>
                        <Box className="small text-muted">
                          Selected File: {file?.name ?? ""}
                        </Box>
                      </label>
                      <Box>
                        <input
                          type="file"
                          name="ctl00$ContentPlaceHolder1$FileUpload1"
                          id="source_file"
                          className="form-control d-none"
                          accept=".zip"
                          data-bs-trigger="hover"
                          data-bs-toggle="tooltip"
                          aria-label="CTRL click to select Multiple files"
                          onChange={(e) =>
                            setFile(
                              e.currentTarget.files !== null &&
                                e.currentTarget.files.length > 0
                                ? e.currentTarget.files.item(0) ?? undefined
                                : undefined
                            )
                          }
                        />
                        <button
                          type="button"
                          id="uploadBtn"
                          className="btn btn-sm btn-secondary"
                          aria-invalid="false"
                          onClick={openFileDlg}
                        >
                          Upload File
                        </button>
                      </Box>
                    </Box>
                  </Box>
                </Box>
              </Box>
            </Box>
          </Box>
        </Box>
        <Box className="d-flex justify-content-center gap-2">
          <input
            type="submit"
            name="ctl00$ContentPlaceHolder1$btnSubmit"
            value="Verify and Publish"
            id="ContentPlaceHolder1_btnSubmit"
            className="btn btn-primary p-2 px-4 btn-submit"
            data-bs-toggle="tooltip"
            data-bs-trigger="hover"
            aria-label="Submit source code for verification"
            onClick={handleVerify}
          />
        </Box>
      </Box>
    </Container>
  );
};
